import React, {ReactNode, useCallback, useEffect, useState} from "react";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import MapView from "./MapView";
import "./App.css";
import ControlView from "./ControlView";
import {type WalkMap} from "./types";
import EditContainerView from "./EditContainerView";
import "bootstrap/dist/css/bootstrap.min.css";
import {Formik} from "formik";
import PointsTable from "./PointsTable";
import {EditContext} from "./EditContext";
import Form from "react-bootstrap/Form";
import ImageField from "./ImageField";
import AvatarsView from "./AvatarsView";
import {uploadImage} from "./cloudinary";
import {initializeApp} from "firebase/app";
import {
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  GoogleAuthProvider,
  User,
  Auth,
} from "firebase/auth";
import {Button, Nav} from "react-bootstrap";
import {
  getFirestore,
  Firestore,
  doc,
  getDoc,
  setDoc,
  getDocs,
  collection,
} from "firebase/firestore";

const FIREBASE_CONFIG = {
  apiKey: "AIzaSyC94fMebfxMyL_uTKzdxJOiDxt8oSFtUyw",
  authDomain: "walkways-46e92.firebaseapp.com",
  projectId: "walkways-46e92",
  storageBucket: "walkways-46e92.firebasestorage.app",
  messagingSenderId: "863594312833",
  appId: "1:863594312833:web:c68bb18a3038e3e944c980",
};

const App = () => {
  const [updatingCoordinate, setUpdatingCoordinate] = useState<number | null>(
    null,
  );
  const [editNode, setEditNode] = useState<ReactNode>();
  const [user, setUser] = useState<User | null>(null);
  const [auth, setAuth] = useState<Auth | null>(null);
  const [db, setDB] = useState<Firestore | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [tab, setTab] = useState<"map" | "avatars">("map");

  const queryClient = useQueryClient();

  const {
    data: savedMaps = [],
    isLoading: savedMapsLoading,
    isError: savedMapsError,
  } = useQuery({
    queryKey: ["walkMaps"],
    queryFn: async () => {
      const querySnapshot = await getDocs(collection(db!, "WalkMaps"));
      return querySnapshot.docs.map(doc => doc.data() as WalkMap);
    },
    enabled: Boolean(db),
  });

  useEffect(() => {
    const firebaseApp = initializeApp(FIREBASE_CONFIG);
    const firebaseAuth = getAuth(firebaseApp);

    onAuthStateChanged(firebaseAuth, user => {
      setUser(user);
      setLoaded(true);
    });
    setAuth(firebaseAuth);
    setDB(getFirestore(firebaseApp));
  }, []);

  const signInHandler = useCallback(() => {
    if (!auth) {
      return;
    }

    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider)
      .then(result => {
        // This gives you a Google Access Token. You can use it to access the Google API.
        // const credential = GoogleAuthProvider.credentialFromResult(result);
        //const token = credential.accessToken;
        setUser(result.user);
      })
      .catch(error => {
        console.log("Failed to login user: ", error);
      });
  }, [auth]);

  if (!loaded) {
    return null;
  }

  if (!user) {
    return (
      <div className="App-sign-in">
        <Button variant="primary" onClick={() => signInHandler()}>
          Sign In
        </Button>
      </div>
    );
  }

  return (
    <div className="App">
      <Nav
        variant="pills"
        className="App-tabs"
        activeKey={tab}
        onSelect={key => setTab(key === "avatars" ? "avatars" : "map")}>
        <Nav.Item>
          <Nav.Link eventKey="map">Map</Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="avatars">Avatars</Nav.Link>
        </Nav.Item>
      </Nav>
      <Formik
        initialValues={{
          id: "",
          name: "",
          points: [],
          pxToKM: 1,
          saved: false,
        }}
        onSubmit={async (values: WalkMap, {setSubmitting}) => {
          if (!db) {
            console.log("Could not save to firestore, no db setup");
            setSubmitting(false);
            return;
          }

          const newMap = !values.saved;

          // The map id is free text, so make sure a new map does not
          // overwrite a map that another creator saved with the same id.
          if (newMap) {
            if (!values.id.trim() || values.id.includes("/")) {
              window.alert(
                "Enter a map ID in Settings. The ID cannot be empty or contain a slash.",
              );
              setSubmitting(false);
              return;
            }

            try {
              const existingMap = await getDoc(doc(db, "WalkMaps", values.id));
              if (existingMap.exists()) {
                window.alert(
                  `A map with the ID "${values.id}" already exists. Change the ID in Settings, then save again.`,
                );
                setSubmitting(false);
                return;
              }
            } catch (e) {
              console.log("Failed to check map id: ", e);
              window.alert(`Could not check if the map ID is free: ${e}`);
              setSubmitting(false);
              return;
            }
          }

          let imageURL = "";

          if (values.saved) {
            imageURL = values.image?.url || "";
          } else {
            try {
              imageURL = await uploadImage(values.image?.url || "");
            } catch (e) {
              console.log("Failed to upload image: ", e);
              setSubmitting(false);
              return;
            }
          }

          if (!(imageURL && values.image)) {
            console.log("Failed to determine image url");
            setSubmitting(false);
            return;
          }

          const docRef = doc(db, "WalkMaps", values.id);

          try {
            await setDoc(docRef, {
              ...values,
              image: {...values.image, url: imageURL},
              saved: true,
            });
          } catch (e) {
            console.log("failed to submit: ", e);
            window.alert(`Failed to save the map: ${e}`);
            setSubmitting(false);
            return;
          }

          // Change the form values only after the write succeeds, so a failed
          // save leaves a new map unsaved and its ID editable.
          values.image.url = imageURL;
          values.saved = true;

          queryClient.setQueryData(["walkMaps"], (current: WalkMap[] = []) =>
            newMap
              ? [...current, values]
              : current.map(savedMap =>
                  savedMap.id === values.id ? values : savedMap,
                ),
          );

          setSubmitting(false);
        }}>
        {({values}) => (
          <EditContext.Provider
            value={{
              editNode,
              setEditNode,
              updatingCoordinate,
              setUpdatingCoordinate,
              savedMaps,
              savedMapsLoading,
              savedMapsError,
            }}>
            {values.image ? (
              <MapView />
            ) : (
              <Form.Group className="App-image-field mb-3">
                <Form.Label>Map Image: </Form.Label>
                <ImageField />
              </Form.Group>
            )}
            <ControlView />
            <PointsTable />
            <EditContainerView />
          </EditContext.Provider>
        )}
      </Formik>
      {tab === "avatars" && db ? <AvatarsView db={db} /> : null}
    </div>
  );
};

export default App;
